import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isHotkey from 'is-hotkey';
import { Editable, withReact, Slate } from 'slate-react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { deserializeToSlate, HOTKEYS, toggleMark, withHtml } from '~/common/utils/richText';
import { Leaf, Element, MarkButton } from '~/ui/components/RichText';
import DOMPurify from 'dompurify';
import { slateToHtml } from '@slate-serializers/html';
import {
  ActionButton,
  ActionButtonWrapper,
  CharCount,
  ControlsPanel,
  Preview,
  EditorContainer,
} from './RichText.styled';

export type RichTextProps = {
  placeholder?: string;
  onChange?: (value: any) => void;
  handleAddImage?: () => void;
  value?: any;
  small?: boolean;
  showActionButton?: boolean;
  controlsVisible?: boolean;
  maxLength?: number;
};

export const RichText = forwardRef(
  ({
    placeholder,
    onChange,
    value,
    handleAddImage,
    small,
    showActionButton,
    controlsVisible,
    maxLength,
  }: RichTextProps) => {
    const containerRef = useRef<any>(null);
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const editor = useMemo(() => withHtml(withReact(withHistory(createEditor()))), []);
    const [animation, setAnimation] = useState(null);
    const [focused, setFocused] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [charCount, setCharCount] = useState(0);

    function removeEmptyTags(htmlString) {
      const regex = /<p[^>]*>(\s|&nbsp;)*<\/p>/gi;
      return htmlString.replace(regex, '');
    }

    const handleFocus = useCallback(() => {
      setFocused(true);
    }, [setFocused]);

    const handleOnChange = useCallback(
      (values) => {
        const html = removeEmptyTags(slateToHtml(values));
        setCurrentValue(html);
        onChange?.(html);
        if (maxLength) {
          setCharCount((html || '').replace(/<[^>]*>/g, '').length);
        }
      },
      [maxLength],
    );

    const handleClickOutside = useCallback(
      (event) => {
        if (containerRef.current && !containerRef.current?.contains(event.target) && focused !== null) {
          setFocused(false);
        }
      },
      [focused, containerRef, setFocused, animation],
    );

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside]);

    useEffect(() => {
      if (focused !== null) {
        setAnimation(focused ? 'show' : 'hide');
      }
    }, [focused]);

    useEffect(() => {
      if (value !== currentValue) {
        setCurrentValue(value);
      }
    }, [value]);

    return (
      <>
        <EditorContainer ref={containerRef} className="editor-container" small={small} data-testid="rich-text-editor">
          {value !== currentValue ? (
            <span>{value}</span>
          ) : (
            <Slate editor={editor} initialValue={deserializeToSlate(value)} onValueChange={handleOnChange}>
              <Editable
                onFocus={handleFocus}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder={placeholder}
                onKeyDown={(event) => {
                  if (maxLength) {
                    const currentText = (slateToHtml(editor.children) || '').replace(/<[^>]*>/g, '');
                    if (currentText.length >= maxLength && event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
                      event.preventDefault();
                      return;
                    }
                  }
                  Object.keys(HOTKEYS).forEach((hotkey) => {
                    if (isHotkey(hotkey, event as any)) {
                      event.preventDefault();
                      toggleMark(editor, HOTKEYS[hotkey]);
                    }
                  });
                }}
                onPaste={(event) => {
                  if (maxLength) {
                    const currentText = (slateToHtml(editor.children) || '').replace(/<[^>]*>/g, '');
                    const pastedText = event.clipboardData.getData('text/plain');
                    if (currentText.length >= maxLength) {
                      event.preventDefault();
                      return;
                    }
                    if (currentText.length + pastedText.length > maxLength) {
                      event.preventDefault();
                      editor.insertText(pastedText.slice(0, maxLength - currentText.length));
                    }
                  }
                }}
              />

              <ControlsPanel controlsVisible={controlsVisible} animation={animation} data-test-id="controls-panel">
                <MarkButton height={12} format="bold" icon="Bold" data-testid="rich-text-bold-button" />
                <MarkButton height={12} format="italic" icon="Italic" data-testid="rich-text-italic-button" />
                <MarkButton format="underline" icon="Underline" data-testid="rich-text-underline-button" />
                {!!handleAddImage && <MarkButton icon="ImageFile" onClick={handleAddImage} />}

                {showActionButton && (
                  <ActionButtonWrapper>
                    <ActionButton data-testid="rich-text-publish-button">Publicar</ActionButton>
                  </ActionButtonWrapper>
                )}
              </ControlsPanel>
            </Slate>
          )}
        </EditorContainer>
        {maxLength && (
          <CharCount atLimit={charCount >= maxLength}>
            {charCount}/{maxLength}
          </CharCount>
        )}
      </>
    );
  },
);

type RenderRichTextProps = {
  html: string;
  bordered?: boolean;
  children?: React.ReactNode | string;
};

export function RenderRichText({ html, bordered, children }: RenderRichTextProps) {
  function createMarkup(html) {
    return {
      __html: DOMPurify.sanitize(html),
    };
  }

  return (
    <Preview bordered={bordered}>
      <div dangerouslySetInnerHTML={createMarkup(html)} />
      {children}
    </Preview>
  );
}
